/**
 * Platform session. Bridge owns NO auth UI — signup / verify / login all live in
 * the platform auth app, and this console only reads who is signed in.
 *
 * The session is the platform's httpOnly `accessToken` cookie: it is never
 * readable from JS, so "am I signed in" is answered by asking the platform
 * (`GET /v1/me`) with credentials included, not by inspecting storage.
 */
import { PLATFORM_BASE, PLATFORM_AUTH_APP } from './config';
import { PRODUCT } from './planGate';

export interface PlatformSession {
  user: { id?: string; email?: string; first_name?: string; last_name?: string };
  org: { id?: string; name?: string; status?: string };
  role: string | null;
  /** False until the "tell us about you" step is done — the platform owns it. */
  profileComplete: boolean;
  /** The product websites the org bought; null = no restriction. Read it
   *  through ./planGate, never directly. */
  subscribed_products?: string[] | null;
}

/** The signed-in session, or null when there is no valid cookie. */
export async function fetchSession(): Promise<PlatformSession | null> {
  let res: Response;
  try {
    res = await fetch(`${PLATFORM_BASE}/v1/me`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    // Platform unreachable — surfaced as "cannot verify session", never as a
    // silent signed-out state (which would look identical to a logged-out user).
    throw new Error(`Cannot reach the platform at ${PLATFORM_BASE}`);
  }
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error(`Session check failed (${res.status})`);

  const body = (await res.json()) as { data?: Record<string, unknown> };
  const d = (body.data ?? body) as Record<string, any>;
  return {
    user: d.user ?? {},
    org: d.org ?? {},
    role: d.memberships?.[0]?.role ?? d.role ?? null,
    profileComplete: d.profile_complete !== false,
    subscribed_products: d.subscribed_products,
  };
}

/** Send the visitor to the platform auth app, returning here afterwards. */
export function goToLogin(): void {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${PLATFORM_AUTH_APP}/?return_to=${returnTo}`;
}

/**
 * Send a signed-in user whose org did not buy Bridge to the platform's "not in
 * your plan" page (see ./planGate). The session stays: the org's own console
 * still calls Bridge. `replace`, so Back does not land on a Bridge that would
 * only send them here again.
 */
export function goToNotInPlan(): void {
  const url = new URL('not-in-plan', PLATFORM_AUTH_APP.replace(/\/*$/, '/'));
  url.searchParams.set('product', PRODUCT);
  window.location.replace(url.toString());
}

/** Finish the profile step (first/last/company) on the platform. */
export function goToCompleteProfile(): void {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${PLATFORM_AUTH_APP}/?mode=complete-profile&return_to=${returnTo}`;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${PLATFORM_BASE}/v1/auth/logout`, { method: 'POST', credentials: 'include' });
  } finally {
    goToLogin();
  }
}
